import { isEqual } from "lodash";
import * as React from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { Link } from "react-router-dom";

import { reactor, ReactorConfiguration } from "@app/arc";
import { Message, MessageType, NotificationContextProps, withNotificationContext } from "@app/notifications";
import { ProjectRunStatusChangePayload, ZipCodeAnalysisStatusChangeNotificationMessagePayload } from "@app/notifications/message";
import { ProjectTileData } from "@app/projects";
import { ProjectRunConfigurationStatus } from "@app/projects/enumerators";
import { CompositeAggregates } from "@app/projects/network-assessment/composite/CompositeAggregates";
import { RegionGranularity } from "@app/region-sets";
import { FilteredResource, Resource, ResourceType } from "@app/resources";
import { formatDateTime, TooltipWrapper } from "@app/shared";
import { Roles, UserContextProps, withUserContext } from "@app/user";
import { buildReactorConfig, handleUpdateSummary, ProjectTileState, renderProjectStatus } from "./Helpers";
import ProjectActionMenu from "./ProjectActionMenu";
import { ButtonToggle } from "@app/shared/ButtonToggle";
import { getArcReactorDescription } from "@app/projects/ArcReactor";
import { ProjectType } from "./ProjectType";

interface ProjectTileProps {
  /** The initial project resource. Don't access this prop. Use this.state.projectResource which will use the updated resource from summary when available. */
  initialProjectResource: Resource;

  /** The inital project data. Don't access this prop. Use this.state.summary instead, as it will update if a job completes which updates the project. */
  initialSummary: ProjectTileData;

  /** Event triggered after successful archive/unarchive */
  onArchived: (projectId: number) => void;

  /** Event triggered after successful hide/unhide */
  onHidden: (projectId: number) => void;

  /** Event triggered on switching member sets for dual membership projects */
  onMemberSetChange: (projectId: number, showSupplementary: boolean) => void;

  /** Event triggered after project options are updated. */
  onUpdateProjectOptions: (summary: ProjectTileData) => void;

  filteredProjectResource: FilteredResource;

  setProjectIsReadOnly: (projectIds: number[]) => void;

  /** Whether to show a primary or supplementary run config for dual membership projects */
  showSupplementaryData: boolean;
}

type ProjectTileCombinedProps = ProjectTileProps & UserContextProps & RouteComponentProps & NotificationContextProps;

class ProjectTile extends React.Component<ProjectTileCombinedProps, ProjectTileState> {
  private svgRef = React.createRef<SVGSVGElement>();
  private readonly isZipProject = this.props.initialProjectResource.resourceType === ResourceType.ZipAnalysisProject;
  private readonly projectTypeClassName = this.isZipProject ? "qes-zip-project" : "qes-county-project";

  constructor(props: ProjectTileCombinedProps) {
    super(props);

    this.state = {
      projectResource: props.initialProjectResource,
      sidePanelArchiveWarningVisible: false,
      summary: null,
      triggerHide: null,
      triggerOpen: null,
    };
  }

  public async componentDidMount() {
    if (this.props.initialSummary) {
      this.setState((prevState) => ({
        projectResource: this.props.initialSummary.resource
          ? this.props.initialSummary.resource
          : prevState.projectResource,
        summary: this.props.initialSummary,
      }),
      );
    }

    this.props.subscribe(this.handleNotifications);
    reactor(this.svgRef.current, this.config());
  }

  public componentDidUpdate(prevProps: ProjectTileProps) {
    reactor(this.svgRef.current, this.config());

    if (!isEqual(prevProps.initialSummary, this.props.initialSummary)) {
      this.setState((prevState) => ({
        projectResource: this.props.initialSummary && this.props.initialSummary.resource
          ? this.props.initialSummary.resource
          : prevState.projectResource,
        summary: this.props.initialSummary,
      }),
      );
    }
  }

  public render() {
    const isShared = this.state.projectResource.isShared;
    const projectType = this.isZipProject ? "Zip Code Network Assessment" : "Network Assessment";
    return (
      <>
        {(!isShared || null) && (this.renderForProjectOwner(projectType))}
        {(isShared || null) && (this.renderForProjectViewer(projectType))}
      </>
    );
  }

  public renderForProjectOwner(projectType: string) {
    let defaultTab = "";
    if (!!this.state.summary?.adequacy || this.isZipProject) {
      defaultTab = "adequacy";
    } else if (!!this.state.summary?.accuracy) {
      defaultTab = "accuracy";
    }

    let tileUrl = `/projects/${this.state.projectResource.resourceId}/${defaultTab}`;
    let tileQuerystring = '';

    if (defaultTab === "adequacy") {
      switch (localStorage.getItem("viewbyfilter")) {
        case "County": tileQuerystring = "?viewBy=BY_COUNTY"; break;
        case "Specialty": tileQuerystring = "?viewBy=BY_SPECIALTY"; break;
        case "All": tileQuerystring = "?viewBy=ALL"; break;
        default: tileQuerystring = "?viewBy=BY_STATE";
      }
    }

    if (this.props.filteredProjectResource && this.props.filteredProjectResource.filterPath) {
      tileUrl += this.props.filteredProjectResource.filterPath;

    }

    const menu = (
      <ProjectActionMenu
        {...this.props}
        filteredProjectResource={this.props.filteredProjectResource}
        ownerOptions={{
          onArchived: this.props.onArchived,
          onUpdateProjectOptions: () => handleUpdateSummary(this.props.onUpdateProjectOptions, this.state.projectResource, this.props.filteredProjectResource),
          projectResource: this.state.projectResource,
          projectTileData: this.state.summary,
        }}
        projectOwned={true}
        setProjectIsReadOnly={this.props.setProjectIsReadOnly}
      />
    );

    const arcReactorDescription = getArcReactorDescription(this.config());

    return (
      <div className={`col-12 col-sm-6 col-lg-4 col-xl-3 col-xxl-2dot4 col-xxxl-2 col-4xl-1dot7 col-5xl-1dot5 col-6xl-1dot33 ${this.projectTypeClassName}`}>
        <div className="tile-item ellipsis">
          <div className="box-style-1 white-bg project-tile-top">
            {menu}
            <TooltipWrapper text={this.state.projectResource.name} triggerWidth="100%">
              <h2 className="lead font-weight-bold mtb0 color-tuna" data-toggle="tooltip" data-placement="top">
                <Link to={{ pathname: tileUrl, search: tileQuerystring, state: { initialDualMembershipMode: this.state.summary && this.state.summary.runConfigurationStatus !== ProjectRunConfigurationStatus.Failed && this.state.summary.isDualMembership } }}>
                  {this.state.projectResource.name}
                </Link>
              </h2>
            </TooltipWrapper>

            <div className="hgt47">
              {this.state.summary &&
                this.state.summary.lastModifiedDate &&
                <p className="color-logan">
                  <Link to={{ pathname: tileUrl, state: { initialDualMembershipMode: this.state.summary && this.state.summary.runConfigurationStatus !== ProjectRunConfigurationStatus.Failed && this.state.summary.isDualMembership } }}>
                    {formatDateTime(this.state.summary.lastModifiedDate)}
                  </Link>
                </p>
              }
            </div>

            <div className="row">
              <div className="col-12 plr30 pb15">
                <div className="svg-container">
                  <svg ref={this.svgRef} role="img" aria-label={`${arcReactorDescription}`}>
                    <title>Arc Reactor</title>
                    <desc>{arcReactorDescription}</desc>
                  </svg>
                </div>
              </div>
            </div>

            <div className="row dual-membership-buttons">
              {this.state.summary && this.state.summary.isDualMembership && !this.isZipProject && (
                <>
                  <ButtonToggle
                    handleClick={(isPrimary) => this.props.onMemberSetChange(this.state.summary.projectId, !isPrimary)}
                    hideTooltips={true}
                    isPrimarySelected={!this.props.showSupplementaryData}
                    primaryButtonLabel={this.state.summary.memberSetAlias}
                    secondaryButtonLabel={this.state.summary.companionMemberSetAlias}
                    testId={this.state.projectResource.resourceId.toString()}
                  />
                </>
              )}
            </div>

            <ProjectType projectType={projectType} className={this.projectTypeClassName} />

            <div className="hgt27">
              {this.state.summary && this.state.summary.owner &&
                <p className="text-muted">
                  {`Created by: ${this.state.summary.owner}`}
                </p>
              }
            </div>

            <div className="hgt21">
              {this.state.summary && renderProjectStatus(this.state.summary, true, this.props.user.roles)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  public renderForProjectViewer(projectType: string) {
    const defaultTab = this.state.summary && this.state.summary.adequacy ? "adequacy" : "accuracy";

    let tileUrl = `/projects/${this.state.projectResource.resourceId}/${defaultTab}`; // TODO: BXN - Add logic to determine whether composite tab is available
    if (this.props.filteredProjectResource && this.props.filteredProjectResource.filterPath) {
      tileUrl += this.props.filteredProjectResource.filterPath;
    }

    const viewerMenu = (
      <ProjectActionMenu
        {...this.props}
        filteredProjectResource={this.props.filteredProjectResource}
        projectOwned={false}
        showingHidden={this.state.projectResource.isHidden}
      />
    );

    const arcReactorDescription = getArcReactorDescription(this.config());

    return (
      <div className={`col-12 col-sm-6 col-lg-4 col-xl-3 col-xxl-2dot4 col-xxxl-2 col-4xl-1dot7 col-5xl-1dot5 col-6xl-1dot33 ${this.projectTypeClassName}`}>
        <div className="tile-item ellipsis">
          <div className="box-style-1 white-bg project-tile-top">
            {viewerMenu}
            <TooltipWrapper text={this.state.projectResource.name} triggerWidth="100%">
              <h2 className="mtb0 lead font-weight-bold color-tuna" data-toggle="tooltip" data-placement="top" title={this.state.projectResource.name}>
                <Link to={{ pathname: tileUrl, state: { initialDualMembershipMode: this.state.summary && this.state.summary.isDualMembership } }}>
                  {this.state.projectResource.name}
                </Link>
              </h2>
            </TooltipWrapper>

            <div className="hgt47">
              {this.state.summary &&
                this.state.summary.lastModifiedDate &&
                <p className="color-logan">
                  <Link to={{ pathname: tileUrl, state: { initialDualMembershipMode: this.state.summary && this.state.summary.isDualMembership } }}>
                    {formatDateTime(this.state.summary.lastModifiedDate)}
                  </Link>
                </p>
              }
            </div>

            <div className="row">
              <div className="col-12 plr30 pb15">
                <div className="svg-container">
                  <svg ref={this.svgRef} role="img" aria-label={arcReactorDescription}>
                    <title>Arc Reactor</title>
                    <desc>{arcReactorDescription}</desc>
                  </svg>
                </div>
              </div>
            </div>

            <div className="row dual-membership-buttons">
              {this.state.summary && this.state.summary.isDualMembership && (
                <>
                  <ButtonToggle
                    handleClick={(isPrimary) => this.props.onMemberSetChange(this.state.summary.projectId, !isPrimary)}
                    hideTooltips={true}
                    isPrimarySelected={!this.props.showSupplementaryData}
                    primaryButtonLabel={this.state.summary.memberSetAlias}
                    secondaryButtonLabel={this.state.summary.companionMemberSetAlias}
                    testId={this.state.projectResource.resourceId.toString()}
                  />
                </>
              )}
            </div>

            <ProjectType projectType={projectType} className={this.projectTypeClassName} />

            <div className="hgt27">
              {this.state.summary && this.state.summary.owner &&
                <p className="text-muted">
                  {`Created by: ${this.state.summary.owner}`}
                </p>
              }
            </div>

            <div className="hgt42">
              {this.state.summary && renderProjectStatus(this.state.summary, true, this.props.user.roles)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  private handleNotifications = async (message: Message) => {
    if (message) {
      switch (message.notificationType as MessageType) {
        case MessageType.ProjectRunStatusChange:
        case MessageType.ProcessingProjectionAnalysis:
        case MessageType.ProcessingSpecialtyGroupExceptions:
        case MessageType.ProcessingProviderChanges:
        case MessageType.ProcessingProspectMarketNcp:
          const projectRunStatusChangePayload = message.payload as ProjectRunStatusChangePayload;

          if (this.state.summary &&
            this.state.summary.projectId === projectRunStatusChangePayload.projectId &&
            (!this.state.summary.resource.isShared || projectRunStatusChangePayload.isPublished || this.props.user.roles.indexOf(Roles.PREVIEW) > -1) &&
            (this.state.summary.runConfigurationStatus !== projectRunStatusChangePayload.status || projectRunStatusChangePayload.status === ProjectRunConfigurationStatus.Succeeded)) {
            await handleUpdateSummary(this.props.onUpdateProjectOptions, this.state.projectResource, this.props.filteredProjectResource);
          }
          break;
        case MessageType.ZipCodeAnalysisStatusChangeNotification:
          const zipPayload = message.payload as ZipCodeAnalysisStatusChangeNotificationMessagePayload;
          const summary = this.state.summary;
          if (summary &&
            summary.projectId === zipPayload.projectId &&
            (!summary.resource.isShared || this.props.user.roles.indexOf(Roles.PREVIEW) > -1) &&
            (summary.runConfigurationStatus !== zipPayload.projectRunStatus || zipPayload.projectRunStatus === ProjectRunConfigurationStatus.Succeeded)) {
            await handleUpdateSummary(this.props.onUpdateProjectOptions, this.state.projectResource, this.props.filteredProjectResource);
          }
          break;
      }
    }
  }

  private tileUriBase = () => {
    return `/projects/${this.state.projectResource.resourceId}`;
  }

  private config: () => ReactorConfiguration | undefined = () => {
    if (this.isZipProject) {
      return {
        accuracy: null,
        adequacy: {
          blue: 0,
          green: 100,
          index: "N/A",
          monochrome: true,
          projected: null,
          projectedIndex: null,
          red: 100,
          route: "",
          yellow: 100,
        },
        attestation: null,
        composite: null,
        compositeOverride: "adequacy",
        monochrome: true,
      };
    }
    //TODO: Temporary until re-aggregation is done on the fly for projection and specialty group exception changes
    if (this.state.summary) {
      const r = this.state.summary;
      const a = r.isDualMembership && this.props.showSupplementaryData ? r.companionAdequacy : r.adequacy;

      // TODO: Replace mock data with real data
      const compositeAggregates: CompositeAggregates = {
        accuracyAggregate: r.accuracy ? {
          accuracyIndex: r.accuracy.accuracyIndex,
          bands: r.accuracy.bands,
          defaultDisplayOrder: 0,
          displayText: "",
          key: "",
          linkSuffix: "",
          providerCount: r.accuracy.providerCount,
          providerFailPercentage: r.accuracy.providerFailPercentage,
          providerPassPercentage: r.accuracy.providerPassPercentage,
          regionCode: "",
          regionGranularity: RegionGranularity.Unknown,
          regionSetDisplayName: "",
          regionSetId: -1,
          resultGroups: [],
          subRegionCode: "",
        } : null,
        adequacyAggregate: a ? {
          accessMetCount: 0,
          accessNotMetCount: 0,
          accessTotalCount: 0,
          adequacyIndex: a.adequacyIndex,
          adequacyIndexInformationalOnly: 0,
          adequacyIndexWithExceptions: 0,
          adequacyIndexWithInformational: 0,
          approvedExceptionCount: 0,
          approvedExceptionNoGapCount: 0,
          defaultDisplayOrder: 0,
          displayText: "",
          fullMemberCountWithAccess: 0,
          fullMemberCountWithoutAccess: 0,
          gapCount: 0,
          key: "",
          linkSuffix: "",
          noneMetCount: 0,
          percentGaps: a.percentGaps,
          percentPassAccessOnly: a.percentPassAccessOnly,
          percentPassBoth: a.percentPassBoth,
          percentPassByException: a.percentPassByException,
          percentPassNone: a.percentPassNone,
          percentPassServicingOnly: a.percentPassServicingOnly,
          projectedAdequacyIndex: a.projectedAdequacyIndex,
          projectedPercentPassBoth: a.projectedPercentPassBoth,
          regionCode: "",
          regionGranularity: RegionGranularity.Unknown,
          regionSetDisplayName: "",
          regionSetId: -1,
          servicingMetCount: 0,
          servicingNotMetCount: 0,
          servicingOnlyMetCount: 0,
          servicingTotalCount: 0,
          subRegionCode: "",
        } : null,
        attestationAggregate: null,
        compositeIndex: 0,
      };

      const multipleAggregates = [compositeAggregates.accuracyAggregate, compositeAggregates.adequacyAggregate, compositeAggregates.attestationAggregate]
        .filter((aggr) => !!aggr)
        .length > 1;

      const baseConf = buildReactorConfig(this.state.summary, this.props.showSupplementaryData);
      const conf: ReactorConfiguration = {
        ...baseConf,
        accuracy: compositeAggregates.accuracyAggregate ? {
          bands: compositeAggregates.accuracyAggregate.bands.map((band) => ({
            color: band.color,
            name: band.name,
            value: band.inspectionCount * band.weightSummation,
          })).filter((b) => b.value > 0),
          indeterminantText: "N/A",
          index: compositeAggregates.accuracyAggregate.accuracyIndex,
          isIndeterminant: compositeAggregates.accuracyAggregate.providerCount === 0,
          route: `${this.tileUriBase()}/accuracy`,
        } : null,
        adequacy: compositeAggregates.adequacyAggregate ? {
          ...baseConf.adequacy,
          route: `${this.tileUriBase()}/adequacy`,
        } : null,
        composite: Math.round(((compositeAggregates.accuracyAggregate && compositeAggregates.accuracyAggregate.accuracyIndex || 0) +
          (compositeAggregates.adequacyAggregate && compositeAggregates.adequacyAggregate.adequacyIndex || 0)) / 2 * 100) / 100,
        compositeOverride:
          multipleAggregates ? undefined :
            compositeAggregates.accuracyAggregate ? "accuracy" :
              compositeAggregates.adequacyAggregate ? "adequacy" :
                compositeAggregates.attestationAggregate ? "attestation" : undefined,

        //if we only have one aggregate then use it as the composite route
        compositeRoute: `${this.tileUriBase()}/${multipleAggregates ? "composite" :
          compositeAggregates.accuracyAggregate ? "accuracy" :
            compositeAggregates.adequacyAggregate ? "adequacy" :
              compositeAggregates.attestationAggregate ? "attestation" : ""}`,
        onRouteChange: this.props.history.push,
      };

      return conf;
    }

    return undefined;
  }
}

const WrappedProjectTile = withRouter(withUserContext(withNotificationContext(ProjectTile)));

export default WrappedProjectTile as React.ComponentClass<ProjectTileProps>;
